/**
 * Feature Configuration (HA-108)
 * Maps features to required subscription tiers for UI gating
 */

import { SubscriptionTier } from '../models/Subscription';

/**
 * Feature identifiers for tier-gating
 */
export type FeatureId =
  | 'telehealth'
  | 'messaging'
  | 'scheduling'
  | 'clientManagement'
  | 'dailyLogs'
  | 'checkins'
  | 'templates'
  | 'attachments'
  | 'advancedAnalytics'
  | 'customForms'
  | 'documentManagement'
  | 'groupScheduling'
  | 'teamManagement'
  | 'customIntegrations'
  | 'prioritySupport'
  | 'workflowAutomation'
  | 'multiLocation'
  | 'whiteLabeling'
  | 'enterpriseIntegrations'
  | 'broadcasts'
  | 'groupChat'
  | 'groupPrograms'
  | 'smsBroadcasts'
  | 'unifiedCalendar'
  | 'practiceBranding'
  | 'billingManagement'
  | 'auditLogs'
  // HA-237: Orphaned component feature IDs
  | 'communityForum'
  | 'resourceLibrary';

/**
 * Feature configuration with tier requirements and metadata
 */
export interface FeatureConfig {
  id: FeatureId;
  name: string;
  description: string;
  requiredTier: SubscriptionTier;
  requiresFlag?: string; // Optional feature flag from Cognito claims
}

/**
 * All features with their tier requirements
 * Tiers: Free (0), Basic (10), Plus (20), Premium (30), Enterprise (40)
 */
export const featureConfigs: Record<FeatureId, FeatureConfig> = {
  // Free tier features
  clientManagement: {
    id: 'clientManagement',
    name: 'Client Management',
    description: 'Basic client records and profiles',
    requiredTier: SubscriptionTier.Free,
  },
  scheduling: {
    id: 'scheduling',
    name: 'Scheduling',
    description: 'Basic appointment scheduling',
    requiredTier: SubscriptionTier.Free,
  },

  // Basic tier features (Grow equivalent)
  telehealth: {
    id: 'telehealth',
    name: 'Telehealth',
    description: 'Video conferencing for virtual appointments',
    requiredTier: SubscriptionTier.Basic,
    requiresFlag: 'telehealth',
  },
  messaging: {
    id: 'messaging',
    name: 'Secure Messaging',
    description: 'HIPAA-compliant client messaging',
    requiredTier: SubscriptionTier.Basic,
  },
  dailyLogs: {
    id: 'dailyLogs',
    name: 'Daily Logs',
    description: 'Client nutrition and activity tracking',
    requiredTier: SubscriptionTier.Basic,
  },
  checkins: {
    id: 'checkins',
    name: 'Check-ins',
    description: 'Wellness check-in tracking',
    requiredTier: SubscriptionTier.Basic,
  },
  templates: {
    id: 'templates',
    name: 'Templates',
    description: 'Document and form templates',
    requiredTier: SubscriptionTier.Basic,
  },
  attachments: {
    id: 'attachments',
    name: 'Attachments',
    description: 'File attachments and documents',
    requiredTier: SubscriptionTier.Basic,
  },
  groupChat: {
    id: 'groupChat',
    name: 'Group Chat',
    description: 'Group messaging for programs',
    requiredTier: SubscriptionTier.Basic,
  },
  groupScheduling: {
    id: 'groupScheduling',
    name: 'Group Scheduling',
    description: 'Schedule group sessions',
    requiredTier: SubscriptionTier.Basic,
  },

  // Plus tier features (Scale equivalent)
  advancedAnalytics: {
    id: 'advancedAnalytics',
    name: 'Advanced Analytics',
    description: 'Detailed reports and insights',
    requiredTier: SubscriptionTier.Plus,
  },
  customForms: {
    id: 'customForms',
    name: 'Custom Forms',
    description: 'Create custom intake forms',
    requiredTier: SubscriptionTier.Plus,
  },
  documentManagement: {
    id: 'documentManagement',
    name: 'Document Management',
    description: 'Advanced document organization',
    requiredTier: SubscriptionTier.Plus,
  },
  teamManagement: {
    id: 'teamManagement',
    name: 'Team Management',
    description: 'Multi-practitioner coordination',
    requiredTier: SubscriptionTier.Plus,
  },
  broadcasts: {
    id: 'broadcasts',
    name: 'Broadcasts',
    description: 'Practice-wide announcements',
    requiredTier: SubscriptionTier.Plus,
  },
  groupPrograms: {
    id: 'groupPrograms',
    name: 'Group Programs',
    description: 'Multi-session group programs',
    requiredTier: SubscriptionTier.Plus,
  },
  practiceBranding: {
    id: 'practiceBranding',
    name: 'Practice Branding',
    description: 'Custom branding for your practice',
    requiredTier: SubscriptionTier.Plus,
  },
  smsBroadcasts: {
    id: 'smsBroadcasts',
    name: 'SMS Broadcasts',
    description: 'Send SMS notifications',
    requiredTier: SubscriptionTier.Plus,
  },
  // HA-237: Community Forum and Resource Library features
  communityForum: {
    id: 'communityForum',
    name: 'Community Forum',
    description: 'Practice community discussions and threads',
    requiredTier: SubscriptionTier.Plus,
  },
  resourceLibrary: {
    id: 'resourceLibrary',
    name: 'Resource Library',
    description: 'Shared resource and file management',
    requiredTier: SubscriptionTier.Plus,
  },

  // Premium tier features
  customIntegrations: {
    id: 'customIntegrations',
    name: 'Custom Integrations',
    description: 'Third-party API integrations',
    requiredTier: SubscriptionTier.Premium,
  },
  prioritySupport: {
    id: 'prioritySupport',
    name: 'Priority Support',
    description: 'Dedicated support team',
    requiredTier: SubscriptionTier.Premium,
  },
  workflowAutomation: {
    id: 'workflowAutomation',
    name: 'Workflow Automation',
    description: 'Automated task workflows',
    requiredTier: SubscriptionTier.Premium,
  },
  multiLocation: {
    id: 'multiLocation',
    name: 'Multi-Location',
    description: 'Manage multiple practice locations',
    requiredTier: SubscriptionTier.Premium,
  },

  // Enterprise tier features
  whiteLabeling: {
    id: 'whiteLabeling',
    name: 'White Labeling',
    description: 'Remove HealioSpace branding',
    requiredTier: SubscriptionTier.Enterprise,
  },
  enterpriseIntegrations: {
    id: 'enterpriseIntegrations',
    name: 'Enterprise Integrations',
    description: 'EHR and enterprise system integration',
    requiredTier: SubscriptionTier.Enterprise,
  },
  unifiedCalendar: {
    id: 'unifiedCalendar',
    name: 'Unified Calendar',
    description: 'Team-wide calendar view',
    requiredTier: SubscriptionTier.Enterprise,
  },
  billingManagement: {
    id: 'billingManagement',
    name: 'Billing Management',
    description: 'Invoice and billing controls',
    requiredTier: SubscriptionTier.Enterprise,
  },
  auditLogs: {
    id: 'auditLogs',
    name: 'Audit Logs',
    description: 'HIPAA compliance audit trail',
    requiredTier: SubscriptionTier.Enterprise,
  },
};

/**
 * Get the display name for a subscription tier
 */
export const getTierName = (tier: SubscriptionTier): string => {
  switch (tier) {
    case SubscriptionTier.Free:
      return 'Free';
    case SubscriptionTier.Launch:
      return 'Launch';
    case SubscriptionTier.Basic:
      return 'Basic';
    case SubscriptionTier.Plus:
      return 'Plus';
    case SubscriptionTier.Premium:
      return 'Premium';
    case SubscriptionTier.Enterprise:
      return 'Enterprise';
    default:
      return 'Unknown';
  }
};

/**
 * Get tier from string name (case-insensitive)
 */
export const getTierFromName = (name: string | null): SubscriptionTier => {
  if (!name) return SubscriptionTier.Free;

  const normalized = name.toLowerCase();

  switch (normalized) {
    case 'free':
      return SubscriptionTier.Free;
    case 'launch':
      return SubscriptionTier.Launch;
    case 'basic':
    case 'grow':
      return SubscriptionTier.Basic;
    case 'plus':
    case 'scale':
      return SubscriptionTier.Plus;
    case 'premium':
      return SubscriptionTier.Premium;
    case 'enterprise':
      return SubscriptionTier.Enterprise;
    default:
      return SubscriptionTier.Free;
  }
};

/**
 * Check if a tier meets the minimum requirement
 */
export const tierMeetsRequirement = (
  userTier: SubscriptionTier,
  requiredTier: SubscriptionTier
): boolean => {
  return userTier >= requiredTier;
};

/**
 * Get all features available at a given tier
 */
export const getFeaturesForTier = (tier: SubscriptionTier): FeatureConfig[] => {
  return Object.values(featureConfigs).filter((config) =>
    tierMeetsRequirement(tier, config.requiredTier)
  );
};

/**
 * Get features that require upgrade from current tier
 */
export const getUpgradeFeatures = (currentTier: SubscriptionTier): FeatureConfig[] => {
  return Object.values(featureConfigs).filter((config) => config.requiredTier > currentTier);
};
